import { connect } from 'react-redux';
import { useCallback } from 'react';
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays';
import { getIn } from 'final-form';

import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { Row } from 'UIPackage/FormModule/components/Row';
import { TextareaField } from 'UIPackage/MainModule/components/TextareaField';
import { DatePickerField } from 'UIPackage/MainModule/components/DatePickerField';
import { DecisionResultDocument } from 'DecisionPackage/DecisionResultModule/components/DecisionResultDocument';
import { TextInputField } from 'UIPackage/MainModule/components/TextInputField';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { SelectInputField } from 'UIPackage/MainModule/components/SelectInputField';
import { IDictionarySelectOption } from 'CorePackage/DictionariesModule/interfaces/IDictionarySelectOption';
import { IParticipantPosition } from 'APIPackage/DictionariesModule/interfaces/IParticipantPosition';
import { IKnmErknmFormNoteLawsuit } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormNoteLawsuit';
import { CloseButton } from 'UIPackage/MainModule/components/CloseButton';
import { IDecisionFormRef } from 'DecisionPackage/MainModule/interfaces/IDecisionFormRef';
import { Button } from 'UIPackage/MainModule/components/Button';
import { IDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionForm';
import { DecisionEliminatedViolations } from 'DecisionPackage/DecisionEliminatedViolationsModule/components/DecisionEliminatedViolations';
import { IKnmErknmFormViolatedRequirementsNpa } from 'KnmErknmPackage/MainModule/interfaces/IKnmErknmFormViolatedRequirementsNpa';
import { IDecisionResultDecisionForm } from 'DecisionPackage/MainModule/interfaces/IDecisionResultDecisionForm';
import {
  getDecisionParticipantPositionsSelectOptions,
  getKnmIsDecisionStatusTypeCompleted,
} from 'DecisionPackage/MainModule/selectors/decisionSelectors';
import { getUserRoleIsSupervisor } from 'AuthPackage/MainModule/selectors/authSelectors';
import { KnmResultDecisionStatusEnum } from 'APIPackage/KnmModule/enums/KnmResultDecisionStatusEnum';

import { ExternalSystemSignInfoBlock } from '../../../../CorePackage/MainModule/components/ExternalSystemSignInfoBlock';
import { ReactQuillEditorField } from '../../../../UIPackage/MainModule/components/ReactQuillEditorField';

interface IDecisionResultItemContentProps {
  isWatchOnly: boolean;
  resultDecision: IDecisionResultDecisionForm | undefined;
  resultDecisionIndex: number;
  violatedRequirementsNpa: IKnmErknmFormViolatedRequirementsNpa[] | undefined;
  formRef: IDecisionFormRef | undefined;
  name: string;
  isPrescription: boolean;
}

interface IDecisionResultItemContentStoreProps {
  positionsSelectOptions?: IDictionarySelectOption<IParticipantPosition>[];
  isSupervisor: boolean;
  isDecisionStatusCompleted: boolean;
}

const DecisionResultItemContentWithoutStore = ({
  isWatchOnly,
  resultDecision,
  positionsSelectOptions,
  violatedRequirementsNpa,
  formRef,
  isSupervisor,
  isDecisionStatusCompleted,
  name,
  resultDecisionIndex,
  isPrescription,
}: IDecisionResultItemContentProps & IDecisionResultItemContentStoreProps) => {
  const isDisableResult = !isSupervisor || !isDecisionStatusCompleted;
  const isDisable = isWatchOnly || isDisableResult || resultDecision?.status === KnmResultDecisionStatusEnum.COMPLETED;

  const push = formRef?.current.mutators.push;
  const remove = formRef?.current.mutators.remove;

  const addNote = useCallback(() => {
    push?.(`${name}.notes`, { isNew: true });
  }, [push]);
  const makeRemoveNote = useCallback((index: number) => () => remove?.(`${name}.notes`, index), [remove]);

  if (!isPrescription && resultDecision?.injunction) {
    formRef?.current.change(`${name}.injunction` as keyof IDecisionForm);
  }

  if (!isPrescription && resultDecision?.eliminatedMandatoryRequirements) {
    formRef?.current.change(`${name}.eliminatedMandatoryRequirements` as keyof IDecisionForm);
  }

  const formValue = getIn(formRef?.current.getState().values as IDecisionForm, name);

  return (
    <>
      {!isPrescription && (
        <FieldBlock
          label="Описание решения"
          button={
            <Button
              className="shared-btn-add"
              text="Добавить"
              type="button"
              variant="link"
              color="primary"
              disabled={isDisable}
              onClick={addNote}
            />
          }
        >
          <FieldArray name={`${name}.notes`}>
            {({ fields, meta }: FieldArrayRenderProps<IKnmErknmFormNoteLawsuit, HTMLElement>) => {
              return (
                <>
                  {fields.map((fieldName, index) => {
                    return (
                      <Row key={fieldName}>
                        <ReactQuillEditorField
                          className="shared-deleting-row"
                          id={`${fieldName}.valueReactQuillEditor`}
                          name={`${fieldName}.valueReactQuillEditor`}
                          placeholder="Введите значение"
                          disabled={isDisable}
                          handleCustomChange={(e: string) => {
                            // @ts-ignore
                            formRef?.current?.change(`${fieldName}.value` as never, e);
                          }}
                        />

                        {resultDecision?.isNew && (
                          <CloseButton className="shared-deleting-row-btn" onClick={makeRemoveNote(index)} />
                        )}
                      </Row>
                    );
                  })}
                  {typeof meta.error === 'string' && meta.touched && (
                    <p className="shared-input-error shared-row__small-margin">{meta.error}</p>
                  )}
                </>
              );
            }}
          </FieldArray>
        </FieldBlock>
      )}

      {isPrescription && (
        <>
          <Row>
            <FieldBlock label="Информация о проведении мероприятий по предотвращению причинения вреда (ущерба) охраняемым законом ценностям">
              <ReactQuillEditorField
                name={`${name}.injunction.noteReactQuillEditor`}
                disabled={isDisable}
                handleCustomChange={(e: string) => {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  formRef?.current?.change(`${name}.injunction.note` as any, e);
                }}
              />
            </FieldBlock>
          </Row>
          <Row>
            <FieldBlock label="Срок исполнения решения" size="col-4">
              <DatePickerField name={`${name}.injunction.dateResolved`} disabled={isDisable} />
            </FieldBlock>
          </Row>
        </>
      )}
      {(isPrescription || !!resultDecision?.eliminatedMandatoryRequirements?.length) && (
        <DecisionEliminatedViolations
          formRef={formRef}
          watchOnly={isDisable}
          requirementsNpa={violatedRequirementsNpa}
          requirementEliminationsFieldName={`${name}.eliminatedMandatoryRequirements`}
        />
      )}
      <Row>
        <DecisionResultDocument
          watchOnly={isDisable}
          attachmentsNumber={resultDecision?.document?.attachments?.length || 0}
          name={`${name}.document`}
          resultDecisionIndex={resultDecisionIndex}
        />
      </Row>
      <Row title="Дата и номер решения (действия) контрольного (надзорного) органа">
        <FieldBlock size="half" label="Дата решения" placementLabel="left" color="black">
          <DatePickerField className="shared-row-field" name={`${name}.date`} disabled={isDisable} />
        </FieldBlock>
        <FieldBlock size="half" label="Номер решения" placementLabel="left" color="black">
          <TextInputField name={`${name}.number`} disabled={isDisable} />
        </FieldBlock>
      </Row>
      {formValue?.systemSignatureUsed ? (
        <Row>
          <ExternalSystemSignInfoBlock
            systemSignatureDateTime={formValue.systemSignatureDateTime}
            systemSignatureTitle={formValue.systemSignatureTitle}
          />
        </Row>
      ) : (
        <>
          <Row title="Должностное лицо контрольного (надзорного) органа, подписавшее (осуществившее) решение">
            <FieldBlock size="half">
              <TextInputField
                name={`${name}.fioSigner`}
                disabled={isDisable}
                placeholder="Введите ФИО  должностного лица"
              />
            </FieldBlock>
            <FieldBlock size="half">
              <SelectInputField
                disabled={isDisable}
                id={`${name}.titleSigner`}
                name={`${name}.titleSigner`}
                placeholder="Выберите должность"
                options={positionsSelectOptions}
                isSearchable
              />
            </FieldBlock>
          </Row>
        </>
      )}
      <Row>
        <FieldBlock label="Сведения о контролируемом лице, в отношении которого принято решение (осуществлено действие)">
          <TextareaField name={`organization.originalName`} disabled />
        </FieldBlock>
      </Row>
    </>
  );
};

const mapStateToProps = (state: IRootState): IDecisionResultItemContentStoreProps => ({
  positionsSelectOptions: getDecisionParticipantPositionsSelectOptions(state),
  isSupervisor: getUserRoleIsSupervisor(state),
  isDecisionStatusCompleted: getKnmIsDecisionStatusTypeCompleted(state),
});

export const DecisionResultItemContent = connect(mapStateToProps)(DecisionResultItemContentWithoutStore);
